import { useMemo } from "react";
import states from "@/constants/states";
import { Hr_PastTrack, Hr_PastTrackLocation } from "@/graphql/gql/graphql";

interface FilteredTracksParams {
  tracks: Hr_PastTrack[];
  searchTerm?: string;
}

type stateKey = keyof typeof states;
type locationKey = keyof Hr_PastTrackLocation;

const useFilteredTracks = ({ tracks, searchTerm }: FilteredTracksParams) => {
  const sortTracks = (tracksToSort: Record<string, Hr_PastTrack[]>) =>
    Object.entries(tracksToSort || {})
      .sort()
      .reduce(
        (allTracks, [key, value]) => ({ ...allTracks, [key]: value }),
        {} as Record<string, Hr_PastTrack[]>
      );

  const groupBy = (tracksToGroup: Hr_PastTrack[], key: string) =>
    tracksToGroup?.reduce(
      (allTracks, track) => {
        const fullKey =
          key === "state"
            ? states[(track?.location?.[key] ?? "").trim() as stateKey]
            : track?.location?.[key as locationKey]?.trim();
        const tempTracks = allTracks;
        if (fullKey) {
          (tempTracks[fullKey] = tempTracks[fullKey] || []).push(track);
        }
        return tempTracks;
      },
      {} as Record<string, Hr_PastTrack[]>
    );

  const filteredTracks = useMemo(() => {
    if (searchTerm && searchTerm.length > 0) {
      const upperCaseSearchTerm = searchTerm.toLocaleUpperCase();
      return tracks.filter(
        (track) =>
          track.code?.toLocaleUpperCase().includes(upperCaseSearchTerm) ||
          track.name?.toLocaleUpperCase().includes(upperCaseSearchTerm) ||
          track.location?.country
            ?.toLocaleUpperCase()
            ?.includes(upperCaseSearchTerm) ||
          track.location?.state
            ?.toLocaleUpperCase()
            ?.includes(upperCaseSearchTerm)
      );
    }

    return tracks;
  }, [searchTerm, tracks]);

  const usaTracks = sortTracks(
    groupBy(
      filteredTracks?.filter((track) => track?.location?.country === "USA"),
      "state"
    ) || {}
  );
  const nonUsaTracks = sortTracks(
    groupBy(
      filteredTracks?.filter((track) => track?.location?.country !== "USA"),
      "country"
    ) || {}
  );

  return {
    usaTracks,
    nonUsaTracks,
  };
};

export default useFilteredTracks;
